import { useState, useCallback, useMemo } from 'react';
import {
  MRT_ShowHideColumnsButton as MrtShowHideColumnsButton,
  MRT_ToggleDensePaddingButton as MrtToggleDensePaddingButton,
  MRT_ToggleFiltersButton as MrtToggleFiltersButton,
  MRT_ToggleGlobalFilterButton as MrtToggleGlobalFilterButton,
  useMaterialReactTable,
} from 'material-react-table';
import {
  Box,
  IconButton,
  Tooltip,
  Button,
} from '@mui/material';
import {
  CompareArrows,
  Download as DownloadIcon,
  NoteAdd as NoteAddIcon,
  Share as ShareIcon,
  GetApp as GetAppIcon,
  TableChart as TableChartIcon,
  KeyboardDoubleArrowDown as ExpandHeaderIcon,
  ShowChart as ShowChartIcon,
} from '@mui/icons-material';
import FileOpenIcon from '@mui/icons-material/FileOpen';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import { useFileDownload } from '../../Hooks/useFileDownload';
import { useFileCompare } from '../../Hooks/useFileCompare';
import { useAPI } from '../../Hooks/useAPIs';
import { useFileApi } from '../../Hooks/useFileApi';
import { savedownloadFiles } from '../../utils/CommonUtils';
import { exportTableToCSV, applyColumnDefaults } from './tableUtils';
import { MediaPreviewPanel } from './MediaPreviewPanel';
import { useNotification } from '../NotificationContext';

export const useReusableTable = ({
  columns,
  data,
  rowCount,
  onPaginationChange,
  enableRowSelection = true,
  onRowSelectionChange,
  getRowId,
  isLoading = false,
  enableRowActions = true,
  renderRowActions,
  renderBottomToolbarCustomActions,
  initialPageSize = 100,
  initialPageIndex = 0,
  maxHeight = 'calc(100vh - 250px)',
  onPreview,
  enableDefaultPreview = true,
  enableDefaultDownload = true,
  enableDefaultAddToCart = true,
  enableRowExpansion = true,
  enableCompareFiles = true,
  enableCSVExport = false,
  csvExportFilename,
  pagination: externalPagination,
  sorting: externalSorting,
  onSortingChange,
  defaultSorting,
  downloadingRows: externalDownloadingRows,
  setDownloadingRows: externalSetDownloadingRows,
  paginationLabels = { rowsPerPage: 'Rows per page' },
  maxPages = 10,
  ...additionalProps
}) => {
  if (typeof getRowId !== 'function') {
    throw new Error('useReusableTable: getRowId function is required.');
  }

  const { generateDownloadToken, getDownloadUrl } = useFileApi();
  const navigate = useNavigate();

  const [internalPagination, setInternalPagination] = useState({
    pageIndex: initialPageIndex,
    pageSize: initialPageSize,
  });
  const pagination = externalPagination || internalPagination;
  
  const [rowSelection, setRowSelection] = useState({});

  const setPagination = externalPagination && onPaginationChange ? onPaginationChange : setInternalPagination;

  const [internalSorting, setInternalSorting] = useState(defaultSorting ?? []);
  const isControlledSorting = !!externalSorting && !!onSortingChange;
  const sorting = isControlledSorting ? externalSorting : internalSorting;
  const setSorting = isControlledSorting ? onSortingChange : setInternalSorting;
  const isManualSorting = isControlledSorting;

  const [internalDownloadingRows, setInternalDownloadingRows] = useState({});
  const downloadingRows = externalDownloadingRows || internalDownloadingRows;
  const setDownloadingRows = externalSetDownloadingRows || setInternalDownloadingRows;

  const { apiCall } = useAPI();
  const { downloadByFilenames } = useFileDownload(data);
  const { handleCompare } = useFileCompare(data);

  const notify = useNotification();

  const showSnackbar = useCallback((message, severity = 'info') => {
    notify({ message, severity });
  }, [notify]);

  const resolveFileLocation = useCallback((row) => {
    return row?.file_location || row?.filename ||  '';
  }, []);

  const onAddToCart = useCallback((table) => {
    const selectedRows = table?.getSelectedRowModel?.()?.rows?.map(row => row.original) || [];
    
    if (selectedRows.length === 0) {
      showSnackbar('No files selected', 'warning');
      return;
    }
    
    const validRows = selectedRows.filter(row => {
      const fileLocation = row.file_location || row.file_path || row.location || row.path;
      const fileName = row.filename || row.name;
      return fileName && fileLocation;
    });
    
    if (validRows.length === 0) {
      showSnackbar('Selected files are missing required fields (filename or file_location)', 'error');
      return;
    }
    
    if (validRows.length < selectedRows.length) {
      showSnackbar(`${validRows.length} of ${selectedRows.length} files added to cart. Some files were missing required fields.`, 'warning');
    } else {
      showSnackbar('Files added to cart successfully', 'success');
    }
    
    savedownloadFiles(validRows);
  }, [showSnackbar]);


  const handlePaginationChange = useCallback(updater => {
    const newState = typeof updater === 'function' ? updater(pagination) : updater;
    if (!externalPagination) {
      setInternalPagination(newState);
    }
    if (newState.pageIndex !== pagination.pageIndex) {
      setRowSelection({});
      if (onRowSelectionChange) onRowSelectionChange({});
    }
    if (onPaginationChange) onPaginationChange(newState);
  }, [pagination, externalPagination, onPaginationChange, onRowSelectionChange]);

  const handleSortingChange = useCallback(updater => {
    const newState = typeof updater === 'function' ? updater(sorting) : updater;
    setSorting(newState);
    if (isManualSorting) {
      const resetPagination = { ...pagination, pageIndex: 0 };
      if (!externalPagination) {
        setInternalPagination(resetPagination);
      }
      if (onPaginationChange) onPaginationChange(resetPagination);
      setRowSelection({});
      if (onRowSelectionChange) onRowSelectionChange({});
    }
  }, [sorting, setSorting, isManualSorting, pagination, externalPagination, onPaginationChange, onRowSelectionChange]);

  const handleRowSelectionChange = useCallback(updater => {
    setRowSelection(old => {
      const newState = typeof updater === 'function' ? updater(old) : updater;
      if (onRowSelectionChange) onRowSelectionChange(newState);
      return newState;
    });
  }, [onRowSelectionChange]);

  const handlePreview = useCallback(row => {
    const rowData = row.original || row;
    const filename = resolveFileLocation(rowData);
    const category = row.category ?? rowData?.category;
    const vin = rowData?.vin;
    
    if (!filename) {
      showSnackbar('No file location found for preview', 'error');
      return;
    }

    if (onPreview) {
      const handled = onPreview({ filename, category, vin, rowData });
      if (handled) return;
    }
    
    const params = new URLSearchParams();
    if (category === 'buslog') {
      params.set('filename', filename);
      if (vin) params.set('vin', vin);
      params.set('category', 'buslog');
      window.open(`/bus_log?${params.toString()}`, '_blank');
      return;
    }

    params.set('filename', filename);
    if (vin) params.set('vin', vin);
    if (category) params.set('category', category);
    window.open(`/preview?${params.toString()}`, '_blank');
  }, [resolveFileLocation, showSnackbar, onPreview]);

  const handleCopyShareableLink = useCallback((row) => {
    const rowData = row.original || row;
    const filename = resolveFileLocation(rowData);
    const fileId = rowData?.file_id || null;
    const category = row.category ?? rowData?.category ?? '';
    const vin = rowData?.vin;
    
    if (!filename) {
      showSnackbar('No file location found', 'error');
      return;
    }

    const baseUrl = window.location.origin;
    const params = new URLSearchParams();

    if (fileId) {
      // FilesAPI file details — share by file_id with just the basename
      params.set('file_id', fileId);
      if (filename) params.set('filename', filename.split('/').pop());
    } else {
      // Legacy blob file — share by filename
      params.set('filename', filename);
    }
    if (category) params.set('category', category);
    if (vin) params.set('vin', vin);
    const previewUrl = `${baseUrl}/preview?${params.toString()}`;

    navigator.clipboard.writeText(previewUrl)
      .then(() => {
        showSnackbar('Shareable link copied to clipboard!', 'success');
      })
      .catch(() => {
        showSnackbar('Failed to copy link to clipboard', 'error');
      });
  }, [resolveFileLocation, showSnackbar]);

  const lastWeekDataCategories = [
    'dtc-read', 
    'pn-pull', 'pn-pull-common', 'pn-pull-ipa',
    'event',
    'test-output', 'vsrt-report'
  ];

  const handleViewLastWeekData = useCallback((row) => {
    const rowData = row.original || row;
    const category = rowData?.category;
    const vin = rowData?.vin;

    const endDate = dayjs().endOf('day');
    const startDate = dayjs().subtract(7, 'day').startOf('day');

    const params = new URLSearchParams();

    let route = '';

    if (category === 'dtc-read') {
      params.set('start_date', startDate.format());
      params.set('end_date', endDate.format());
      route = '/dtc_log';
    } else if (['pn-pull', 'pn-pull-common', 'pn-pull-ipa'].includes(category)) {
      params.set('startDate', startDate.format());
      params.set('endDate', endDate.format());
      route = '/part_number_pull';
    } else if (category === 'event') {
      params.set('startDate', startDate.format());
      params.set('endDate', endDate.format());
      route = '/event';
    } else if (['test-output', 'vsrt-report'].includes(category)) {
      params.set('startDate', startDate.format());
      params.set('endDate', endDate.format());
      route = '/test_result_ingestion';
    }

    if (vin) {
      params.set('vin', vin);
    }

    if (route) {
      navigate(`${route}?${params.toString()}`);
    }
  }, [navigate]);

  const handleDownload = useCallback(async row => {
    const rowId = getRowId(row);
    const rowData = row.original || row;
    const filename = resolveFileLocation(rowData);
    const fileId = rowData?.file_id || null;
    const displayName = rowData?.filename || filename?.split('/').pop() || 'file';

    if (!filename) {
      showSnackbar('No filename found for download', 'error');
      return;
    }

    setDownloadingRows(prev => ({ ...prev, [rowId]: true }));
    showSnackbar(`Downloading "${displayName}"...`, 'info');

    try {
      if (fileId) {
        // Step 1 (auth-gated): exchange file_id for a one-time opaque token URL.
        // The token URL — not the SAS URL — is what appears in the Network tab.
        const resp = await apiCall('get', `filesapi_download/${fileId}`);
        const tokenUrl = resp?.data?.download_url;
        if (!tokenUrl) {
          throw new Error('No download token URL returned from server');
        }

        // Step 2: navigate the browser directly to the token URL.
        // The backend validates the token and returns a 302 to Azure Blob.
        // The browser follows the redirect and downloads the file directly.
        // No auth header is needed here — the one-time token is the credential.
        const a = document.createElement('a');
        a.href = tokenUrl;
        a.download = displayName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        // Legacy blob-based download
        await downloadByFilenames([filename]);
      }
      showSnackbar(`"${displayName}" downloaded successfully`, 'success');
    } catch (error) {
      showSnackbar(`Failed to download "${displayName}"`, 'error');
    } finally {
      setDownloadingRows(prev => {
        const upd = { ...prev };
        delete upd[rowId];
        return upd;
      });
    }
  }, [getRowId, downloadByFilenames, resolveFileLocation, setDownloadingRows, showSnackbar]);

  const defaultRenderRowActions = useCallback(({ row }) => {
    const rowData = row.original || row;
    const category = rowData?.category;
    const showLastWeekData = lastWeekDataCategories.includes(category);
    const isBuslog = category === 'buslog';

    return (
      <Box sx={{ display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
        {enableDefaultPreview && (
          <Tooltip title={isBuslog ? "View Buslog Signals" : "Preview"}>
            <IconButton
              aria-label="preview"
              color="info"
              size="small"
              onClick={() => handlePreview(row.original)}
            >
              {isBuslog ? <ShowChartIcon /> : <FileOpenIcon />}
            </IconButton>
          </Tooltip>
        )}
        {enableDefaultDownload && (
          <Tooltip title="Download">
            <IconButton
              aria-label="download"
              color="info"
              size="small"
              onClick={() => handleDownload(row.original)}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Copy Shareable Link">
          <IconButton
            aria-label="copy-shareable-link"
            color="info"
            size="small"
            onClick={() => handleCopyShareableLink(row.original)}
          >
            <ShareIcon />
          </IconButton>
        </Tooltip>
        {showLastWeekData && (
          <Tooltip title="View Last Week Data">
            <IconButton
              aria-label="view-last-week-data"
              color="info"
              size="small"
              onClick={() => handleViewLastWeekData(row.original)}
            >
              <TableChartIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  }, [
    enableDefaultPreview,
    enableDefaultDownload,
    handleDownload,
    handlePreview,
    handleCopyShareableLink,
    handleViewLastWeekData,
    lastWeekDataCategories,
  ]);

  const handleCSVExport = useCallback(() => {
    if (!data || data.length === 0) {
      showSnackbar('No data to export', 'warning');
      return;
    }
    
    const date = new Date();
    const dateStr = date.toISOString().split('T')[0];
    const baseFilename = csvExportFilename || 'table-export.csv';
    const filenameWithoutExt = baseFilename.replace(/\.csv$/, '');
    const filename = `${filenameWithoutExt}-${dateStr}.csv`;
    
    try {
      exportTableToCSV(data, columns, filename);
      showSnackbar('CSV file downloaded successfully', 'success');
    } catch (error) {
      showSnackbar('Failed to export CSV', 'error');
    }
  }, [data, columns, csvExportFilename, showSnackbar]);

  const renderToolbarInternalActions = useCallback(({ table }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {enableDefaultAddToCart && (
          <Button
            variant="contained"
            onClick={() => onAddToCart(table)}
            disabled={!table?.getSelectedRowModel?.()?.rows?.length}
          >
            Add To Cart
            <Box sx={{ display: 'inline-block', width: '8px' }} />
            <NoteAddIcon />
          </Button>
        )}

        {enableCompareFiles && (
          <Button
            variant="contained"
            onClick={() => handleCompare(rowSelection)}
            disabled={Object.keys(rowSelection).length !== 2}
            sx={{ ml: 2 }}
            color="primary"
          >
            Compare Files
            <Box sx={{ display: 'inline-block', width: '8px' }} />
            <CompareArrows />
          </Button>
        )}

        {enableCSVExport && (
          <Button
            variant="contained"
            onClick={handleCSVExport}
            color="primary"
            disabled={!data || data.length === 0}
            startIcon={<GetAppIcon />}
          >
            Export CSV
          </Button>
        )}

        <MrtToggleGlobalFilterButton table={table} />
        <MrtToggleFiltersButton table={table} />
        <MrtShowHideColumnsButton table={table} />
        <MrtToggleDensePaddingButton table={table} />
      </Box>
    </Box>
  ), [
    enableDefaultAddToCart,
    enableCompareFiles,
    enableCSVExport,
    handleCompare,
    handleCSVExport,
    onAddToCart,
    rowSelection,
    data,
  ]);

  const getSelectedFilesFromTable = useCallback((table) => {
    return table
      ?.getSelectedRowModel?.()
      ?.rows?.map(row => resolveFileLocation(row.original))
      .filter(Boolean) || [];
  }, [resolveFileLocation]);

  const handleDownloadInNewTab = useCallback(async selectedFiles => {
    if (!selectedFiles || selectedFiles.length === 0) {
      showSnackbar('No files selected for download', 'warning');
      return;
    }

    try {
      const token = await generateDownloadToken(selectedFiles);
      if (!token) throw new Error('No token returned from API');

      const downloadUrl = `/download?sessionToken=${token}`;
      window.open(downloadUrl, '_blank');
      showSnackbar('Download started successfully', 'success');
    } catch (error) {
      showSnackbar('Error preparing download', 'error');
    }
  }, [generateDownloadToken, showSnackbar]);

  const defaultRenderBottomToolbarCustomActions = useCallback(({ table }) => {
    if (enableCSVExport) {
      return null;
    }
    
    const selectedFiles = getSelectedFilesFromTable(table);
    return (
      <Box sx={{ display: 'flex', gap: '0.5rem', p: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <Tooltip title="Download Selected">
          <span>
            <IconButton
              aria-label="download-selected"
              color="info"
              size="small"
              onClick={() => handleDownloadInNewTab(selectedFiles)}
              disabled={selectedFiles.length < 1}
            >
              <SaveAltIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  }, [getSelectedFilesFromTable, handleDownloadInNewTab, enableCSVExport]);

  const finalRenderBottomToolbarCustomActions = useCallback(props => (
    <>
      {defaultRenderBottomToolbarCustomActions(props)}
      {renderBottomToolbarCustomActions?.(props)}
    </>
  ), [defaultRenderBottomToolbarCustomActions, renderBottomToolbarCustomActions]);

  const renderDetailPanel = enableRowExpansion
    ? ({ row }) => <MediaPreviewPanel file={row.original} />
    : undefined;

  const finalRenderRowActions = renderRowActions || (enableRowActions ? defaultRenderRowActions : undefined);

  const normalizedColumns = useMemo(() => applyColumnDefaults(columns), [columns]);

  const table = useMaterialReactTable({
    columns: normalizedColumns,
    data,
    manualPagination: false,
    manualSorting: isManualSorting,
    rowCount,
    onPaginationChange: handlePaginationChange,
    onSortingChange: handleSortingChange,
    enableRowSelection,
    onRowSelectionChange: handleRowSelectionChange,
    getRowId,
    state: {
      pagination,
      sorting,
      rowSelection,
      isLoading,
      showLoadingOverlay: isLoading,
    },

    muiTableContainerProps: {
      sx: { maxHeight, height: '100%', overflow: 'auto' },
    },

    muiTableHeadProps: {
      sx: {
        position: 'sticky',
        top: 0,
        zIndex: 1,
        backgroundColor: 'var(--mui-palette-background-default)',
        boxShadow: '0 2px 4px -1px rgba(0,0,0,0.1)',
      },
    },


    layoutMode: 'grid-no-grow',
    enableRowVirtualization: false,
    enableSelectAll: true,
    positionToolbarAlertBanner: 'none',
    enableColumnFilterModes: true,
    enableColumnOrdering: true,
    enableGrouping: true,
    enableColumnPinning: true,
    enableFacetedValues: true,
    enableRowActions: enableRowActions && !!finalRenderRowActions,
    renderRowActions: finalRenderRowActions,
    renderBottomToolbarCustomActions: finalRenderBottomToolbarCustomActions,
    renderToolbarInternalActions,
    positionActionsColumn: 'last',
    positionExpandColumn: 'first',

    displayColumnDefOptions: {
      'mrt-row-actions': {
        size: 140,
        minSize: 140,
        maxSize: 140,
        grow: false,
        header: 'Actions',
        muiTableHeadCellProps: {
          align: 'center',
          sx: {
            '& .Mui-TableHeadCell-Content': {
              justifyContent: 'center',
              width: '100%',
            },
          },
        },
        muiTableBodyCellProps: {
          align: 'center',
          sx: {
            textAlign: 'center',
          },
        },
      },
      'mrt-row-expand': {
        size: 50,
        minSize: 50,
        maxSize: 50,
        grow: false,
        header: '',
        Header: () => <ExpandHeaderIcon fontSize="small" sx={{ color: 'action.active' }} />,
        muiTableHeadCellProps: {
          align: 'center',
          sx: { padding: '4px 8px' },
        },
        muiTableBodyCellProps: {
          align: 'center',
          sx: { padding: '4px 8px', overflow: 'visible' },
        },
      },
      'mrt-row-select': {
        size: 50,
        minSize: 50,
        maxSize: 50,
        grow: false,
        muiTableHeadCellProps: {
          align: 'center',
          sx: { padding: '4px 8px' },
        },
        muiTableBodyCellProps: {
          align: 'center',
          sx: { padding: '4px 8px', overflow: 'visible' },
        },
      },
    },

    initialState: {
      columnPinning: {
        left: ['mrt-row-expand', 'mrt-row-select'],
        right: ['mrt-row-actions'],
      },
    },

    muiTableBodyRowProps: useMemo(() => {
      if (typeof additionalProps.highlightRowFn !== 'function') return undefined;
      return ({ row }) => {
        const isHighlighted = additionalProps.highlightRowFn(row.original);
        return isHighlighted
          ? {
              'data-highlighted': 'true',
              sx: {
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                borderLeft: '3px solid #1976d2',
                '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.14)' },
              },
            }
          : {};
      };
    }, [additionalProps.highlightRowFn]),

    muiTableBodyCellProps: useMemo(() => ({ column }) => ({
      sx: {
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        textOverflow: 'unset',
        padding: '10px 12px',
        verticalAlign: 'top',
        lineHeight: 1.4,
      },
    }), []),

    muiTableHeadCellProps: useMemo(() => ({
      sx: {
        backgroundColor: 'var(--mui-palette-background-default)',
        fontWeight: 'bold',
        whiteSpace: 'nowrap',
      },
    }), []),

    muiBottomToolbarProps: useMemo(() => ({
      sx: {
        display: 'flex',
        justifyContent: 'flex-end',
        '& .MuiTablePagination-root': {
          marginLeft: 'auto',
        },
      },
    }), []),

    localization: { rowsPerPage: paginationLabels.rowsPerPage },
    enableExpandAll: false,
    enableExpanding: enableRowExpansion || !!additionalProps.renderDetailPanel,
    getRowCanExpand: (row) => {
      if (row.getIsGrouped()) return true;

      if (additionalProps.renderDetailPanel) return true;

      if (!enableRowExpansion) return false;
      
      const rowData = row.original;
      const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
      const imageFileTypes = ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp', '.svg'];
      
      const fileType = (rowData?.file_type || '').toLowerCase();
      if (fileType && imageFileTypes.includes(fileType)) {
        return true;
      }
      
      const filename = resolveFileLocation(rowData);
      if (!filename) return false;
      const ext = filename.split('.').pop()?.toLowerCase();
      return ext ? imageExtensions.includes(ext) : false;
    },
    renderDetailPanel,

    ...additionalProps,
  });

  return {
    table,
    pagination,
    sorting,
    rowSelection,
    setPagination,
    setSorting,
    setRowSelection,
    downloadingRows,
    setDownloadingRows,
    handleDownload,
    handleCompare,
    onPreview: handlePreview,
    onAddToCart,
  };
};